// CSV import for the network library (gap 2.1). Parses a practice's existing
// plan spreadsheet into normalized plan rows tagged source: 'csv_import'.
// Real parsing (no canned result) with forgiving header + payer matching, so a
// practice's actual export works — not just the bundled sample.

import { MOCK_PAYERS } from '../mock';

const norm = (s) => String(s ?? '').trim();
const lc = (s) => norm(s).toLowerCase();

// Common shorthand a real spreadsheet might use for a payer.
const PAYER_SYNONYMS = {
  uhc: 'unitedhealthcare', 'united healthcare': 'unitedhealthcare', united: 'unitedhealthcare',
  bcbs: 'blue cross blue shield of california', anthem: 'blue cross blue shield of california',
  'blue shield': 'blue cross blue shield of california', 'blue cross': 'blue cross blue shield of california',
  medicare: 'medicare (cms)', cms: 'medicare (cms)', kaiser: 'kaiser permanente', oscar: 'oscar health',
};

export function resolvePayer(name) {
  const n = lc(name);
  if (!n) return null;
  const direct = MOCK_PAYERS.find((p) => lc(p.name) === n || (p.aliases || []).some((a) => lc(a) === n));
  if (direct) return direct;
  const syn = PAYER_SYNONYMS[n];
  if (syn) { const m = MOCK_PAYERS.find((p) => lc(p.name) === syn); if (m) return m; }
  // Loose contains match as a last resort (e.g. "Aetna Inc.").
  return MOCK_PAYERS.find((p) => lc(p.name).includes(n) || n.includes(lc(p.name))) || null;
}

const STATUS_MAP = {
  'in-network': 'in_network', 'in network': 'in_network', innetwork: 'in_network', in: 'in_network', par: 'in_network', participating: 'in_network',
  'out-of-network': 'out_of_network', 'out of network': 'out_of_network', outofnetwork: 'out_of_network', out: 'out_of_network', nonpar: 'out_of_network',
  unknown: 'unknown', '': 'in_network',
};

// CSV line splitter that respects quoted fields and escaped quotes.
function splitCsvLine(line) {
  const out = []; let cur = ''; let q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (q) {
      if (c === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; }
      else cur += c;
    } else if (c === '"') q = true;
    else if (c === ',') { out.push(cur); cur = ''; }
    else cur += c;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

const HEADER_ALIASES = {
  payer: ['payer', 'payor', 'insurance', 'carrier', 'insurer'],
  plan: ['plan', 'plan name', 'planname', 'product', 'product name'],
  type: ['type', 'plan type', 'plantype', 'product type'],
  status: ['status', 'network', 'network status', 'networkstatus', 'participation', 'par status'],
  effectiveDate: ['effective', 'effective date', 'effectivedate', 'start date', 'start'],
};

function mapHeaders(headerRow) {
  const idx = {};
  headerRow.forEach((h, i) => {
    const key = Object.keys(HEADER_ALIASES).find((k) => HEADER_ALIASES[k].includes(lc(h)));
    if (key && idx[key] == null) idx[key] = i;
  });
  return idx;
}

// Parse CSV text → { rows, errors }. Each row: { payerName, payerId, payerResolved,
// planName, planType, status, effectiveDate }.
export function parseNetworkCsv(text) {
  const lines = String(text ?? '').split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return { rows: [], errors: ['The file is empty.'] };

  const header = splitCsvLine(lines[0]);
  const idx = mapHeaders(header);
  if (idx.payer == null || idx.plan == null) {
    return { rows: [], errors: ['Could not find "Payer" and "Plan" columns in the header row.'] };
  }

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = splitCsvLine(lines[i]);
    const payerName = norm(cols[idx.payer]);
    const planName = norm(cols[idx.plan]);
    if (!payerName || !planName) continue;
    const payer = resolvePayer(payerName);
    rows.push({
      payerName,
      payerId: payer?.id || null,
      payerResolved: !!payer,
      planName,
      planType: idx.type != null ? norm(cols[idx.type]) : '',
      status: STATUS_MAP[lc(idx.status != null ? cols[idx.status] : '')] ?? 'in_network',
      effectiveDate: idx.effectiveDate != null ? norm(cols[idx.effectiveDate]) : '',
    });
  }
  return { rows, errors: rows.length ? [] : ['No data rows found below the header.'] };
}
